/**
 * The style bar's words (Miro's text menu): font, size (typed, `#board-size`, or a step at a
 * time), bold, italic, underline, strike, colour (`#board-text-colour`), highlight and
 * alignment, acting on the words selected while they are written, else on the whole item.
 */
import { For, Show, type JSX } from "solid-js";
import { FONT_NAMES, FONTS, TEXT_SIZES } from "../../../../shared/elements";
import { isHexColour, type Bar } from "./bar";
import { FINE_STEPS_BELOW, SIZE_CHOICES } from "../constants";

/** The colour fields' values when the words have none they can show: ink, and a highlighter's yellow. */
const COLOUR_FALLBACK = { color: "#1d1b26", highlight: "#fff3a3" } as const;

/** The alignments, with their pictures' lines. */
const ALIGNMENTS = [
  { id: "left", title: "Words on the left", path: "M2 4h12M2 8h8M2 12h10" },
  { id: "center", title: "Words in the middle", path: "M2 4h12M4 8h8M3 12h10" },
  { id: "right", title: "Words on the right", path: "M2 4h12M6 8h8M4 12h10" },
] as const;

/** Font, size, looks, colours and alignment. */
export function TextTools(props: { bar: Bar }): JSX.Element {
  const { textValue, put, toggle, size, value, set } = props.bar;
  /** `n` within the sizes allowed. */
  const within = (n: number) => String(Math.min(TEXT_SIZES.max, Math.max(TEXT_SIZES.min, n)));
  return (
    <div class="board-text-tools" role="group" aria-label="Words">
      <select id="board-font" aria-label="Font" title="Font" value={textValue("font") || "sans"} onChange={(e) => put("font", e.currentTarget.value === "sans" ? "" : e.currentTarget.value)}>
        <For each={Object.entries(FONT_NAMES)}>{([id, name]) => <option value={id} style={{ "font-family": FONTS[id] }}>{name}</option>}</For>
      </select>
      <div class="board-size-combo">
        <button type="button" data-board-action="smaller" aria-label="Smaller words" title="Smaller" onClick={() => put("size", within(size() - (size() > FINE_STEPS_BELOW ? 2 : 1)))}>
          −
        </button>
        <input
          class="board-size"
          id="board-size"
          type="number"
          list="board-sizes"
          min={TEXT_SIZES.min}
          max={TEXT_SIZES.max}
          aria-label="Size of the words"
          title={`Size (type any from ${TEXT_SIZES.min} to ${TEXT_SIZES.max})`}
          value={size()}
          onChange={(e) => {
            const n = Math.round(Number(e.currentTarget.value));
            if (Number.isFinite(n) && n > 0) put("size", within(n));
            else e.currentTarget.value = String(size());
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            // The key itself goes nowhere: the words being written take the keyboard back, and would get a line break.
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
        />
        <datalist id="board-sizes">
          <For each={SIZE_CHOICES}>{(n) => <option value={n} />}</For>
        </datalist>
        <button type="button" data-board-action="bigger" aria-label="Bigger words" title="Bigger" onClick={() => put("size", within(size() + (size() >= FINE_STEPS_BELOW ? 2 : 1)))}>
          +
        </button>
      </div>
      <span class="board-sep" />
      <button type="button" data-style="weight" data-value="bold" aria-pressed={textValue("weight") === "bold"} aria-label="Bold" title="Bold (Ctrl+B)" onClick={() => toggle("weight", "bold")}>
        <b>B</b>
      </button>
      <button type="button" data-style="italic" data-value="yes" aria-pressed={textValue("italic") === "yes"} aria-label="Italic" title="Italic (Ctrl+I)" onClick={() => toggle("italic", "yes")}>
        <i>I</i>
      </button>
      <button type="button" data-style="underline" data-value="yes" aria-pressed={textValue("underline") === "yes"} aria-label="Underline" title="Underline (Ctrl+U)" onClick={() => toggle("underline", "yes")}>
        <u>U</u>
      </button>
      <button type="button" data-style="strike" data-value="yes" aria-pressed={textValue("strike") === "yes"} aria-label="Strike through" title="Strike through (Ctrl+Shift+X)" onClick={() => toggle("strike", "yes")}>
        <s>S</s>
      </button>
      <span class="board-sep" />
      <label class="board-text-colour" title="Colour of the words">
        <span style={{ "border-bottom-color": textValue("color") || COLOUR_FALLBACK.color }}>A</span>
        <input type="color" id="board-text-colour" aria-label="Colour of the words" value={isHexColour(textValue("color")) ? textValue("color") : COLOUR_FALLBACK.color} onChange={(e) => put("color", e.currentTarget.value)} />
      </label>
      <label class="board-text-colour board-highlight" title="Highlight the words">
        <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" aria-hidden="true">
          <path d="M9.5 2.5l4 4-6 6H5.5l-2-2v-2zM3 14.5h10" />
        </svg>
        <span class="board-highlight-bar" style={{ background: textValue("highlight") || "transparent" }} />
        <input type="color" id="board-highlight" aria-label="Highlight the words" value={isHexColour(textValue("highlight")) ? textValue("highlight") : COLOUR_FALLBACK.highlight} onChange={(e) => put("highlight", e.currentTarget.value)} />
      </label>
      <Show when={textValue("highlight")}>
        <button type="button" data-board-action="no-highlight" aria-label="No highlight" title="No highlight" onClick={() => put("highlight", "")}>
          ⌫
        </button>
      </Show>
      <span class="board-sep" />
      <For each={ALIGNMENTS}>
        {(a) => (
          <button type="button" data-style="align" data-value={a.id} aria-pressed={value("align") === a.id} aria-label={`Words ${a.id}`} title={a.title} onClick={() => set("align", a.id)}>
            <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
              <path d={a.path} />
            </svg>
          </button>
        )}
      </For>
    </div>
  );
}
